(() => {
  if (customElements.get("acqua-spring-v6")) return;

  class AcquaSpringV6 extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.dialog = this.querySelector("[data-asv6-dialog]");
      this.querySelectorAll("[data-asv6-open-offer]").forEach((button) => {
        button.addEventListener("click", () => this.openDetails(false), { signal });
      });
      this.querySelectorAll("[data-asv6-open-delivery]").forEach((button) => {
        button.addEventListener("click", () => this.openDetails(true), { signal });
      });
      this.querySelector("[data-asv6-close]")?.addEventListener("click", () => this.dialog?.close(), { signal });

      this.carousel = this.querySelector(".asv6-photo-carousel");
      this.stack = this.querySelector("[data-asv6-stack]");
      this.slides = [...this.querySelectorAll("[data-asv6-slide]")];
      this.products = [...this.querySelectorAll("[data-asv6-product-index]")];
      this.rotation = this.querySelector("[data-asv6-rotation]");
      this.reduced = matchMedia("(prefers-reduced-motion: reduce)");
      this.index = 0;
      this.busy = false;
      this.onScreen = true;
      this.playing = this.slides.length > 1 && !this.reduced.matches;

      if (this.slides.length > 0) {
        this.stack.classList.remove("asv6-is-turning", "asv6-no-transition");
        this.applyPositions();
        this.querySelector("[data-asv6-next]")?.addEventListener("click", () => {
          this.pause();
          void this.advance(1, true);
        }, { signal });
        this.querySelector("[data-asv6-previous]")?.addEventListener("click", () => {
          this.pause();
          void this.advance(-1, true);
        }, { signal });
        this.rotation?.addEventListener("click", () => {
          this.playing = !this.playing;
          this.updateRotation(2000);
        }, { signal });
        this.carousel.addEventListener("focusin", (event) => {
          if (event.target !== this.rotation) this.pause();
        }, { signal });
        this.addEventListener("shopify:block:select", (event) => {
          const slide = event.target instanceof Element ? event.target.closest("[data-asv6-slide]") : null;
          if (!slide) return;
          this.pause();
          this.index = Number(slide.dataset.asv6Index);
          this.applyPositions();
        }, { signal });
        this.reduced.addEventListener("change", () => {
          if (this.reduced.matches) this.pause();
        }, { signal });
        document.addEventListener("visibilitychange", () => {
          if (document.hidden) this.clearRotation();
          else this.scheduleRotation(2000);
        }, { signal });
        this.observer = new IntersectionObserver((entries) => {
          this.onScreen = entries[0].isIntersecting;
          if (this.onScreen) this.scheduleRotation(2000);
          else this.clearRotation();
        }, { threshold: .3 });
        this.observer.observe(this.carousel);
        this.updateRotation(2000);
      }

      this.setAttribute("data-asv6-ready", "");
      updateFooterLinks();
    }

    disconnectedCallback() {
      this.clearRotation();
      clearTimeout(this.animationTimer);
      this.finishAnimation?.();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.removeAttribute("data-asv6-ready");
      updateFooterLinks();
    }

    clearRotation() {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }

    applyPositions() {
      const total = this.slides.length;
      const positions = ["front", "middle", "back", "rear"];
      this.slides.forEach((slide, slideIndex) => {
        const offset = (slideIndex - this.index + total) % total;
        const active = offset === 0;
        slide.dataset.asv6Position = positions[offset] || "hidden";
        slide.setAttribute("aria-hidden", String(!active));
        if (active) {
          slide.setAttribute("role", "group");
          slide.setAttribute("aria-roledescription", "slide");
          slide.setAttribute("aria-label", `Photograph ${slideIndex + 1} of ${total}`);
        } else {
          slide.removeAttribute("role");
          slide.removeAttribute("aria-roledescription");
          slide.removeAttribute("aria-label");
        }
      });
      this.products.forEach((product) => {
        product.hidden = Number(product.dataset.asv6ProductIndex) !== this.index;
      });
    }

    async advance(delta = 1, manual = false) {
      if (this.busy || !this.isConnected || this.slides.length < 2) return;
      this.busy = true;
      const nextIndex = (this.index + delta + this.slides.length) % this.slides.length;
      const nextImage = this.slides[nextIndex].querySelector("img");
      if (nextImage && !nextImage.complete) {
        try {
          await nextImage.decode();
        } catch {
          this.busy = false;
          return;
        }
      }
      if (!this.controller || this.controller.signal.aborted) return;
      this.stack.dataset.asv6Direction = String(delta);
      if (!this.reduced.matches) {
        this.stack.classList.add("asv6-is-turning");
        await new Promise((resolve) => {
          this.finishAnimation = resolve;
          this.animationTimer = setTimeout(resolve, 620);
        });
        this.finishAnimation = null;
      }
      if (!this.controller || this.controller.signal.aborted) return;
      this.index = nextIndex;
      this.stack.classList.add("asv6-no-transition");
      this.applyPositions();
      this.stack.classList.remove("asv6-is-turning");
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!this.controller || this.controller.signal.aborted) return;
      this.stack.classList.remove("asv6-no-transition");
      this.busy = false;
      if (manual) {
        const caption = this.slides[this.index].querySelector("figcaption span")?.textContent?.trim() || "";
        this.querySelector("[data-asv6-announcement]").textContent =
          `Photograph ${this.index + 1} of ${this.slides.length}. ${caption}.`;
      }
    }

    scheduleRotation(delay = 6000) {
      this.clearRotation();
      if (!this.playing || document.hidden || !this.onScreen || !this.isConnected) return;
      this.rotationTimer = setTimeout(async () => {
        if (this.playing && !document.hidden && this.onScreen && !this.dialog?.open) await this.advance();
        this.scheduleRotation();
      }, delay);
    }

    updateRotation(delay = 6000) {
      if (!this.rotation) return;
      const controls = this.rotation.closest(".asv6-carousel-controls");
      if (this.slides.length < 2) {
        controls.hidden = true;
        return;
      }
      controls.hidden = false;
      this.rotation.setAttribute("aria-label", this.playing ? "Pause photo rotation" : "Play photo rotation");
      this.rotation.setAttribute("aria-pressed", String(this.playing));
      const icon = document.createElement("span");
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = this.playing ? "Ⅱ" : "▶";
      this.rotation.replaceChildren(icon);
      this.scheduleRotation(delay);
    }

    pause() {
      this.playing = false;
      this.updateRotation();
    }

    openDetails(deliveryOnly) {
      this.pause();
      const productTerms = this.querySelector("[data-asv6-product-terms]");
      const heading = this.querySelector("[data-asv6-heading]");
      if (productTerms) productTerms.hidden = deliveryOnly;
      if (heading) heading.textContent = deliveryOnly ? "Delivery details" : "Offer details";
      if (this.dialog && !this.dialog.open) this.dialog.showModal();
    }
  }

  function updateFooterLinks() {
    const present = Boolean(document.querySelector("acqua-spring-v6[data-asv6-ready]"));
    document.querySelectorAll("[data-asv6-footer-details]").forEach((button) => {
      button.hidden = !present;
    });
  }

  customElements.define("acqua-spring-v6", AcquaSpringV6);
  document.addEventListener("click", (event) => {
    const button = event.target instanceof Element ? event.target.closest("[data-asv6-footer-details]") : null;
    if (button && !button.hidden) {
      document.querySelector("acqua-spring-v6[data-asv6-ready]")?.openDetails(false);
    }
  });
  document.addEventListener("shopify:section:load", updateFooterLinks);
  document.addEventListener("DOMContentLoaded", updateFooterLinks, { once: true });
})();
